/**
 * Interacciones del diseño QUBIT 2027 (Claude Design "QUBIT-Landing-A"), portadas de
 * su runtime de prototipo a JS plano: menú móvil de la cabecera, animaciones de
 * entrada ([data-reveal], [data-collapse], [data-count]), órbitas ([data-spin]),
 * marquee ([data-marquee]), pulsos ([data-pulse]), barrido ([data-sweep]), cuenta
 * atrás (#countdown [data-cd]) y "Quedan N días" ([data-left]).
 *
 * Todo es opcional: cada bloque solo actúa si su marcado existe en la página, y las
 * animaciones respetan prefers-reduced-motion.
 */

const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function initMobileMenu() {
    const toggle = document.querySelector('[data-q-menu-toggle]');
    const menu = document.querySelector('[data-q-mobile-menu]');
    if (!toggle || !menu) return;

    const icon = toggle.querySelector('[data-q-menu-icon]');
    const setOpen = (open) => {
        menu.classList.toggle('hidden', !open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (icon) icon.textContent = open ? '×' : '≡';
        document.body.style.overflow = open ? 'hidden' : '';
    };

    toggle.addEventListener('click', () => setOpen(menu.classList.contains('hidden')));
    menu.addEventListener('click', (event) => {
        if (event.target.closest('a')) setOpen(false);
    });
    window.addEventListener('resize', () => {
        if (window.innerWidth >= 1080 && !menu.classList.contains('hidden')) setOpen(false);
    });
}

function initCountdown() {
    const root = document.getElementById('countdown');
    const lefts = document.querySelectorAll('[data-left]');
    if (!root && lefts.length === 0) return;

    const target = root ? new Date(root.dataset.target).getTime() : null;
    let ready = false;

    const tick = () => {
        if (root) {
            const diff = Math.max(0, target - Date.now());
            const values = {
                d: Math.floor(diff / 864e5),
                h: Math.floor(diff / 36e5) % 24,
                m: Math.floor(diff / 6e4) % 60,
                s: Math.floor(diff / 1e3) % 60,
            };
            root.querySelectorAll('[data-cd]').forEach((el) => {
                const key = el.dataset.cd;
                const text = String(values[key]).padStart(key === 'd' ? 3 : 2, '0');
                if (el.textContent === text) return;
                el.textContent = text;
                if (ready && el.animate && !reduceMotion()) {
                    el.animate(
                        [
                            { opacity: 0.2, transform: 'translateY(-40%)', filter: 'blur(4px)' },
                            { opacity: 1, transform: 'none', filter: 'blur(0)' },
                        ],
                        { duration: 450, easing: 'cubic-bezier(.2,.7,.1,1)' },
                    );
                }
            });
        }
        lefts.forEach((el) => {
            const days = Math.ceil((new Date(`${el.dataset.left}T23:59:59`).getTime() - Date.now()) / 864e5);
            el.textContent = days > 0 ? `Quedan ${days} días` : '';
        });
        ready = true;
    };

    tick();
    setInterval(tick, 1000);
}

function collapse(el) {
    const final = el.textContent;
    const glyphs = '01ψΨφ⟩⟨|αβ∣θ';
    const length = final.length;
    const duration = Number(el.dataset.dur) || 1100;
    const start = performance.now() + 120;

    el.animate(
        [
            { textShadow: '-0.14em 0 8px oklch(0.82 0.13 200 / 0.9), 0.14em 0 8px oklch(0.82 0.13 300 / 0.9)', filter: 'blur(1.5px)' },
            { textShadow: '0.06em 0 4px oklch(0.82 0.13 200 / 0.7), -0.06em 0 4px oklch(0.82 0.13 300 / 0.7)', filter: 'blur(0.6px)', offset: 0.6 },
            { textShadow: '0 0 0 transparent, 0 0 0 transparent', filter: 'blur(0)' },
        ],
        { duration: duration + 400, easing: 'cubic-bezier(.5,0,.1,1)' },
    );

    const step = () => {
        const progress = Math.max(0, Math.min(1, (performance.now() - start) / duration));
        let text = '';
        for (let i = 0; i < length; i++) {
            const char = final[i];
            text += char === ' ' || i / length < progress * 1.25 - 0.25 ? char : glyphs[(Math.random() * glyphs.length) | 0];
        }
        el.textContent = progress >= 1 ? final : text;
        if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
}

function count(el) {
    const to = Number(el.dataset.count);
    const suffix = el.dataset.suffix || '';
    const start = performance.now();
    const duration = 1800;
    const format = (x) => String(x).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    const frame = () => {
        const progress = Math.min(1, (performance.now() - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 4);
        el.textContent = format(Math.round(to * eased)) + suffix;
        if (progress < 1) requestAnimationFrame(frame);
    };
    frame();
}

function initEffects() {
    const root = document.documentElement;
    if (!root.animate) return;

    // Movimiento continuo (decorativo): órbitas, marquee, pulsos, barrido.
    if (!reduceMotion()) {
        document.querySelectorAll('[data-spin]').forEach((el) =>
            el.animate([{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }], {
                duration: Number(el.dataset.spin) * 1000,
                iterations: Infinity,
                direction: el.dataset.rev ? 'reverse' : 'normal',
            }),
        );
        document.querySelectorAll('[data-marquee]').forEach((el) =>
            el.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-50%)' }], {
                duration: Number(el.dataset.marquee) * 1000,
                iterations: Infinity,
                direction: el.dataset.rev ? 'reverse' : 'normal',
            }),
        );
        document.querySelectorAll('[data-pulse]').forEach((el) =>
            el.animate(
                [{ boxShadow: '0 0 0 0 oklch(0.82 0.13 200 / 0.6)' }, { boxShadow: '0 0 0 12px oklch(0.82 0.13 200 / 0)' }],
                { duration: 1800, iterations: Infinity, composite: 'add' },
            ),
        );
        document.querySelectorAll('[data-sweep]').forEach((el) =>
            el.animate([{ transform: 'translateX(-100%)' }, { transform: 'translateX(100%)' }], {
                duration: 2600,
                iterations: Infinity,
                easing: 'ease-in-out',
            }),
        );
    }

    // Entradas al hacer scroll: solo si la página activó el modo animado (clase q-js
    // en <html>, puesta en el <head> del layout para no parpadear).
    if (!root.classList.contains('q-js') || reduceMotion() || !('IntersectionObserver' in window)) return;

    const ease = 'cubic-bezier(.2,.7,.1,1)';
    const observer = new IntersectionObserver(
        (entries) =>
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const el = entry.target;
                observer.unobserve(el);
                if (el._qReveal) el._qReveal.play();
                if (el.hasAttribute('data-collapse')) collapse(el);
                if (el.dataset.count) count(el);
            }),
        { threshold: 0.15, rootMargin: '0px 0px -6% 0px' },
    );

    document.querySelectorAll('[data-reveal]').forEach((el) => {
        el._qReveal = el.animate(
            [
                { opacity: 0, transform: 'translateY(32px)', filter: 'blur(10px)' },
                { opacity: 1, transform: 'none', filter: 'blur(0)' },
            ],
            { duration: 1100, easing: ease, fill: 'both', delay: (Number(el.dataset.reveal) || 0) * 90 },
        );
        el._qReveal.pause();
        // Al terminar, se fija la opacidad en línea y se cancela la animación: así no
        // queda un filter/transform activo por elemento (capas de composición de más).
        el._qReveal.onfinish = () => {
            el.style.opacity = '1';
            el._qReveal.cancel();
        };
    });
    document.querySelectorAll('[data-reveal],[data-collapse],[data-count]').forEach((el) => observer.observe(el));
}

function init() {
    initMobileMenu();
    initCountdown();
    initEffects();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
